import {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { getFieldErrorMessage } from "@birdwatch/ui/form";
import { useForm } from "@tanstack/react-form";
import type { Editor } from "@tiptap/core";
import { useEffect, useState } from "react";
import * as z from "zod";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { Label } from "@/components/ui/label";

const linkSchema = z.object({
  title: z.string().min(1, "Text is required"),
  link: z
    .string()
    .min(1, "Link is required")
    .transform((val) => {
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`;
      }
      return val;
    })
    .refine(
      (val) => {
        try {
          // Check if URL is valid
          const url = new URL(val);
          const hostname = url.hostname;

          const parts = hostname.split(".");
          const tld = parts[parts.length - 1];

          return parts.length > 1 && /^[a-z]{2,}$/i.test(tld); // e.g., .com, .net
        } catch {
          return false;
        }
      },
      {
        message: "Please enter a valid link (e.g., example.com or https://...)",
      },
    ),
});

const LinkDialog = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false);

  // Get state from editor storage
  const openLinkDialog = editor?.storage?.customState?.openLinkDialog;
  const selectedText = editor?.storage?.customState?.textSelected;
  const existingLink = editor?.storage?.customState?.existingLink;
  const savedSelection = editor?.storage?.customState?.savedSelection;

  const form = useForm({
    defaultValues: {
      title: "",
      link: "",
    },
    onSubmit: async ({ value }) => {
      if (!editor) return;

      // Validate with Zod
      const result = linkSchema.safeParse(value);
      if (!result.success) return;

      const { link: url, title } = result.data;

      // Close dialog first
      editor.commands.toggleLinkDialog(false);

      // Focus editor and insert link
      editor.commands.focus();

      if (savedSelection) {
        // Replace existing selection with new link
        editor
          .chain()
          .focus()
          .deleteRange({ from: savedSelection.from, to: savedSelection.to })
          .insertContent({
            type: "text",
            text: title,
            marks: [{ type: "link", attrs: { href: url } }],
          })
          .run();
      } else {
        // Insert new link at cursor
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: title,
            marks: [{ type: "link", attrs: { href: url } }],
          })
          .run();
      }

      // Reset state
      editor.commands.resetForLinkDialog();
      form.reset();
    },
  });

  // Sync dialog open state with editor storage
  useEffect(() => {
    if (!editor) return;

    const updateDialogState = () => {
      setOpen(editor.storage.customState.openLinkDialog);
    };

    editor.on("transaction", updateDialogState);

    return () => {
      editor.off("transaction", updateDialogState);
    };
  }, [editor]);

  // Update form values when dialog opens
  useEffect(() => {
    if (openLinkDialog) {
      form.setFieldValue("title", selectedText || "");
      form.setFieldValue("link", existingLink || "");
    }
  }, [openLinkDialog, selectedText, existingLink]);

  const handleOpenChange = (value: boolean) => {
    editor?.commands.toggleLinkDialog(value);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent className="flex flex-col gap-0 p-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <ModalHeader className="text-left">
            <ModalTitle className="p-0">Link</ModalTitle>
            <ModalDescription className="sr-only">
              Link a text to a URL
            </ModalDescription>
          </ModalHeader>
          <div className="p-4 pt-1 space-y-4">
            <form.Field
              name="title"
              validators={{
                onChange: ({ value }) => {
                  const result = z
                    .string()
                    .min(1, "Text is required")
                    .safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.issues[0].message;
                },
              }}
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Text to display</Label>
                  <Input
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Enter link text"
                  />
                  {getFieldErrorMessage(field) && (
                    <p className="text-sm text-text-negative">
                      {getFieldErrorMessage(field)}
                    </p>
                  )}
                </div>
              )}
            />
            <form.Field
              name="link"
              validators={{
                onChange: ({ value }) => {
                  const result = linkSchema.shape.link.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.issues[0].message;
                },
              }}
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Link</Label>
                  <Input
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Paste link here"
                  />
                  {getFieldErrorMessage(field) && (
                    <p className="text-sm text-text-negative">
                      {getFieldErrorMessage(field)}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <ModalFooter>
            <ModalClose asChild>
              <Button type="button" kind="outline" variant="neutral">
                Cancel
              </Button>
            </ModalClose>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  Save
                </Button>
              )}
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default LinkDialog;
